import { useState, useCallback, useEffect, useRef } from "react"
import { useDispatch } from "react-redux"
import {
  getAIDashboardKpiData,
  getAIDashboardWidgets,
  updateAIDashboardWidget,
  saveAIDashboardState,
  getAIDashboardState,
  dataRequested,
  dataReceived,
  kpiRequested,
  kpiReceived
} from "store/aiDashboard"
import { KPI_KEYS, PERIOD_MAP } from "../constants"
import { buildPeriodParams, resolveSavedPeriod, buildPlaceholderWidgets } from "../utils"
import useWidgetFetcher from "./useWidgetFetcher"

export default function useDashboardData() {
  const dispatch = useDispatch()
  const widgetApiIdsRef = useRef([])

  const [widgets, setWidgets] = useState([])
  const [dashboardData, setDashboardData] = useState(null)
  const [kpiData, setKpiData] = useState({})
  const [activeDateFilter, setActiveDateFilter] = useState("This month")
  const [dateRange, setDateRange] = useState([null, null])
  const [singleDate, setSingleDate] = useState(null)
  const [hasNonDefault, setHasNonDefault] = useState(false)

  // Tracks which widget API IDs are still fetching their data (for per-widget spinners)
  const [widgetLoadingApiIds, setWidgetLoadingApiIds] = useState(new Set())
  // Tracks which KPI keys are still fetching (for per-stat spinners)
  const [kpiLoadingKeys, setKpiLoadingKeys] = useState(new Set())

  const setKpiResult = useCallback((kpi, res) => {
    setKpiData((prev) => ({ ...prev, [kpi]: res }))
  }, [])

  const clearKpiLoading = useCallback((kpi) => {
    setKpiLoadingKeys((prev) => {
      const next = new Set(prev)
      next.delete(kpi)
      return next
    })
  }, [])

  const fetchKpi = useCallback((kpi, params) => (
    dispatch(getAIDashboardKpiData(kpi, params))
      .then((res) => setKpiResult(kpi, res))
      .catch(() => setKpiResult(kpi, null))
      .finally(() => clearKpiLoading(kpi))
  ), [dispatch, setKpiResult, clearKpiLoading])

  const fetchAllKpis = useCallback((period, range, single) => {
    const params = buildPeriodParams(period, range, single)
    dispatch(kpiRequested())
    setKpiData({})
    setKpiLoadingKeys(new Set(KPI_KEYS))

    const promises = KPI_KEYS.map((kpi) => fetchKpi(kpi, params))
    Promise.all(promises).finally(() => dispatch(kpiReceived()))
  }, [dispatch, fetchKpi])

  const { fetchAll } = useWidgetFetcher({ setWidgets, setDashboardData, setWidgetLoadingApiIds })

  const fetchDashboardData = useCallback((period, range, single) => {
    const params = buildPeriodParams(period, range, single)
    dispatch(dataRequested())
    setWidgets([])
    setDashboardData(null)
    setWidgetLoadingApiIds(new Set())

    dispatch(getAIDashboardWidgets(params))
      .then((response) => {
        const widgetsArray = (response?.widgets ?? []).sort((a, b) => (a.position ?? 0) - (b.position ?? 0))

        const defaultCount = widgetsArray.filter((widget) => widget.is_default === true).length
        setHasNonDefault(widgetsArray.some((widget) => widget.is_default === false) || defaultCount < 6)
        widgetApiIdsRef.current = widgetsArray.filter((widget) => widget.id).map((widget) => widget.id)

        setWidgets(buildPlaceholderWidgets(widgetsArray))
        setDashboardData({})
        setWidgetLoadingApiIds(new Set(widgetsArray.filter((widget) => widget.id).map((widget) => String(widget.id))))

        dispatch(dataReceived())
        fetchAll(widgetsArray, params)
      })
      .catch(() => {
        setDashboardData(null)
        dispatch(dataReceived())
      })
  }, [dispatch, fetchAll])

  const handlePeriodChange = useCallback(async (period, range, single) => {
    const timePeriod = PERIOD_MAP[period] || "last_30_days"
    const apiIds = widgetApiIdsRef.current
    let dateRangeStr
    if (single) {
      dateRangeStr = single.format("YYYY-MM-DD")
    } else if (range?.[0] && range?.[1]) {
      dateRangeStr = `${range[0].format("YYYY-MM-DD")}T${range[1].format("YYYY-MM-DD")}`
    }

    const widgetPayload = { time_period: timePeriod }
    if (dateRangeStr) widgetPayload.date_range = dateRangeStr
    const statePayload = { global_period: timePeriod }
    if (dateRangeStr) statePayload.date_range = dateRangeStr

    if (apiIds.length > 0) {
      await Promise.all(apiIds.map((id) => dispatch(updateAIDashboardWidget(id, widgetPayload)).catch(() => {})))
    }
    try {
      await dispatch(saveAIDashboardState(statePayload))
    } catch (err) {
      console.error("Failed to save dashboard state:", err)
    }
    fetchDashboardData(period, range, single)
    fetchAllKpis(period, range, single)
  }, [dispatch, fetchDashboardData, fetchAllKpis])

  useEffect(() => {
    const contentEl = document.querySelector(".ant-layout-content")
    const bg = getComputedStyle(document.documentElement).getPropertyValue("--color-surface5").trim()
    if (contentEl) contentEl.style.background = bg
    return () => { if (contentEl) contentEl.style.background = "" }
  }, [])

  useEffect(() => {
    dispatch(getAIDashboardState())
      .then((res) => {
        const resolved = resolveSavedPeriod(res?.state)
        if (resolved.type === "range") {
          setActiveDateFilter(resolved.label)
          setDateRange(resolved.range)
          fetchDashboardData(resolved.label, resolved.range, null)
          fetchAllKpis(resolved.label, resolved.range, null)
        } else if (resolved.type === "single") {
          setActiveDateFilter(resolved.label)
          setSingleDate(resolved.single)
          fetchDashboardData(resolved.label, null, resolved.single)
          fetchAllKpis(resolved.label, null, resolved.single)
        } else {
          setActiveDateFilter(resolved.label)
          fetchDashboardData(resolved.label)
          fetchAllKpis(resolved.label)
        }
      })
      .catch(() => {
        fetchDashboardData("This month")
        fetchAllKpis("This month")
      })
  }, [dispatch, fetchDashboardData, fetchAllKpis])

  return {
    widgets, setWidgets,
    dashboardData,
    kpiData,
    activeDateFilter, setActiveDateFilter,
    dateRange, setDateRange,
    singleDate, setSingleDate,
    hasNonDefault,
    widgetApiIdsRef,
    widgetLoadingApiIds,
    kpiLoadingKeys,
    fetchDashboardData,
    fetchAllKpis,
    handlePeriodChange
  }
}
